import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, ArrowRight, ArrowLeft, Check, Palette, Phone, Mail, MapPin } from 'lucide-react';

const SERVICE_TYPES = [
  'Concrete',
  'Landscaping',
  'Roofing',
  'HVAC',
  'Cleaning',
  'Plumbing',
  'Electrical',
  'Painting',
  'Fencing',
  'Flooring',
  'General Construction',
  'Other'
];

const COLOR_PRESETS = [
  { name: 'Classic Blue', primary: '#1e3a8a', accent: '#fbbf24' },
  { name: 'Forest Green', primary: '#166534', accent: '#f59e0b' },
  { name: 'Slate Gray', primary: '#334155', accent: '#06b6d4' },
  { name: 'Brick Red', primary: '#991b1b', accent: '#fbbf24' },
  { name: 'Royal Purple', primary: '#581c87', accent: '#f97316' },
];

const OnboardingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { onboard } = useAuth();
  
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // User info
    userName: '',
    userEmail: location.state?.email || '',
    // Company info
    company_name: '',
    phone: '',
    email: '',
    address: '',
    // Branding
    brand_primary_color: '#1e3a8a',
    brand_accent_color: '#fbbf24',
    logo_url: '',
    // Services
    service_types_enabled: [],
    // Settings
    default_deposit_percent: 50
  });

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      service_types_enabled: prev.service_types_enabled.includes(service)
        ? prev.service_types_enabled.filter(s => s !== service)
        : [...prev.service_types_enabled, service]
    }));
  };

  const handleNext = () => {
    if (step === 1 && (!formData.userName || !formData.userEmail)) {
      toast.error('Please fill in your name and email');
      return;
    }
    if (step === 2 && !formData.company_name) {
      toast.error('Please enter your company name');
      return;
    }
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (formData.service_types_enabled.length === 0) {
      toast.error('Please select at least one service type');
      return;
    }
    
    setIsLoading(true);
    try {
      const orgData = {
        company_name: formData.company_name,
        phone: formData.phone,
        email: formData.email || formData.userEmail,
        address: formData.address,
        brand_primary_color: formData.brand_primary_color,
        brand_accent_color: formData.brand_accent_color,
        logo_url: formData.logo_url,
        service_types_enabled: formData.service_types_enabled,
        default_deposit_percent: formData.default_deposit_percent
      };
      
      await onboard(orgData, formData.userName, formData.userEmail);
    } catch (error) {
      console.error('Onboarding failed:', error);
      toast.error('Setup failed', { 
        description: error.response?.data?.detail || 'Please try again' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold">
                Let's Get Started
              </h2>
              <p className="text-muted-foreground text-lg mt-2">
                First, tell us about yourself
              </p>
            </div>
            
            <div>
              <Label htmlFor="userName" className="text-lg mb-2 block">
                Your Name
              </Label>
              <Input
                id="userName"
                value={formData.userName}
                onChange={(e) => updateForm('userName', e.target.value)}
                placeholder="John Smith"
                className="input-default"
                data-testid="onboard-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="userEmail" className="text-lg mb-2 block">
                Your Email
              </Label>
              <Input
                id="userEmail"
                type="email"
                value={formData.userEmail}
                onChange={(e) => updateForm('userEmail', e.target.value)}
                placeholder="john@company.com"
                className="input-default"
                data-testid="onboard-email-input"
              />
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold">
                Your Company
              </h2>
              <p className="text-muted-foreground text-lg mt-2">
                This will appear on your estimates and invoices
              </p>
            </div>
            
            <div>
              <Label htmlFor="company_name" className="text-lg mb-2 block">
                Company Name *
              </Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => updateForm('company_name', e.target.value)}
                placeholder="Smith's Concrete LLC"
                className="input-default"
                data-testid="onboard-company-input"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="text-lg mb-2 block">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Phone
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => updateForm('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className="input-default"
                  data-testid="onboard-phone-input"
                />
              </div>
              
              <div>
                <Label htmlFor="companyEmail" className="text-lg mb-2 block">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Business Email
                </Label>
                <Input
                  id="companyEmail"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateForm('email', e.target.value)}
                  placeholder="info@company.com"
                  className="input-default"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="address" className="text-lg mb-2 block">
                <MapPin className="w-4 h-4 inline mr-2" />
                Business Address
              </Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => updateForm('address', e.target.value)}
                placeholder="123 Main St, City, State 12345"
                className="input-default"
              />
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold">
                <Palette className="w-8 h-8 inline mr-2" />
                Brand Colors
              </h2>
              <p className="text-muted-foreground text-lg mt-2">
                Choose colors for your portal (optional)
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => {
                    updateForm('brand_primary_color', preset.primary);
                    updateForm('brand_accent_color', preset.accent);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    formData.brand_primary_color === preset.primary 
                      ? 'border-primary ring-2 ring-primary/20' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`color-preset-${preset.name.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex gap-2 mb-2">
                    <div 
                      className="w-8 h-8 rounded-lg" 
                      style={{ backgroundColor: preset.primary }}
                    />
                    <div 
                      className="w-8 h-8 rounded-lg" 
                      style={{ backgroundColor: preset.accent }}
                    />
                  </div>
                  <p className="text-sm font-medium">{preset.name}</p>
                </button>
              ))}
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div>
                <Label className="text-base mb-2 block">Primary Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.brand_primary_color}
                    onChange={(e) => updateForm('brand_primary_color', e.target.value)}
                    className="w-14 h-14 rounded-xl cursor-pointer border-2 border-gray-200"
                  />
                  <Input
                    value={formData.brand_primary_color}
                    onChange={(e) => updateForm('brand_primary_color', e.target.value)}
                    className="input-default flex-1"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-base mb-2 block">Accent Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.brand_accent_color}
                    onChange={(e) => updateForm('brand_accent_color', e.target.value)}
                    className="w-14 h-14 rounded-xl cursor-pointer border-2 border-gray-200"
                  />
                  <Input
                    value={formData.brand_accent_color}
                    onChange={(e) => updateForm('brand_accent_color', e.target.value)}
                    className="input-default flex-1"
                  />
                </div>
              </div>
            </div>
            
            {/* Preview */}
            <div className="p-6 rounded-2xl border border-gray-200 mt-6">
              <p className="text-sm text-muted-foreground mb-3">Preview:</p>
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-xl"
                  style={{ backgroundColor: formData.brand_primary_color }}
                >
                  {formData.company_name?.charAt(0) || 'C'}
                </div>
                <div>
                  <p className="font-heading font-semibold text-lg">
                    {formData.company_name || 'Your Company'}
                  </p>
                  <button 
                    className="px-4 py-1 rounded-lg text-sm font-bold"
                    style={{ backgroundColor: formData.brand_accent_color }}
                  >
                    New Estimate
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold">
                Your Services
              </h2>
              <p className="text-muted-foreground text-lg mt-2">
                Select the types of work you do
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {SERVICE_TYPES.map((service) => (
                <button
                  key={service}
                  onClick={() => toggleService(service)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    formData.service_types_enabled.includes(service)
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`service-${service.toLowerCase().replace(' ', '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${
                      formData.service_types_enabled.includes(service)
                        ? 'bg-primary border-primary'
                        : 'border-gray-300'
                    }`}>
                      {formData.service_types_enabled.includes(service) && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span className="font-medium">{service}</span>
                  </div>
                </button>
              ))}
            </div>
            
            <div className="p-4 bg-blue-50 rounded-xl mt-6">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> You can always add more services later in Settings.
              </p>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`flex items-center ${s < 4 ? 'flex-1' : ''}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-colors ${
                    s <= step
                      ? 'bg-primary text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s < step ? <Check className="w-5 h-5" /> : s}
                </div>
                {s < 4 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded ${
                      s < step ? 'bg-primary' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>You</span>
            <span>Company</span>
            <span>Branding</span>
            <span>Services</span>
          </div>
        </div>
        
        {/* Form Card */}
        <div className="card-default">
          {renderStep()}
          
          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
            {step > 1 ? (
              <Button
                variant="outline"
                onClick={handleBack}
                className="btn-outline"
                data-testid="onboard-back-btn"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-muted-foreground"
              >
                Cancel
              </Button>
            )}
            
            {step < 4 ? (
              <Button
                onClick={handleNext}
                className="btn-primary"
                data-testid="onboard-next-btn"
              >
                Next
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isLoading || formData.service_types_enabled.length === 0}
                className="btn-secondary"
                data-testid="onboard-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Creating...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    Complete Setup
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
